import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  doc,
  orderBy,
  query,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  ActivityVariableDefinition,
  GlobalActivityCategory,
  GlobalActivityType,
} from "../types/activity";

const COLLECTION_NAME = "adminActivityTypes";

/**
 * Service for managing global activity types (admin-only operations)
 */
export class GlobalActivityService {
  // ==================== CATEGORY OPERATIONS ====================

  /**
   * Fetch all global activity categories
   */
  static async fetchCategories(): Promise<GlobalActivityCategory[]> {
    try {
      const categoriesRef = collection(db, COLLECTION_NAME);
      const q = query(categoriesRef, orderBy("order", "asc"));
      const snapshot = await getDocs(q);

      const categories = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as GlobalActivityCategory[];

      // Filter out soft-deleted categories
      return categories.filter((category) => !category.deleted);
    } catch (error) {
      console.error("Error fetching global activity categories:", error);
      throw new Error("Failed to fetch global activity categories");
    }
  }

  /**
   * Get a single category by ID
   */
  static async getCategoryById(
    categoryId: string
  ): Promise<GlobalActivityCategory | null> {
    try {
      const docRef = doc(db, COLLECTION_NAME, categoryId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as GlobalActivityCategory;
    } catch (error) {
      console.error("Error fetching category by ID:", error);
      throw new Error("Failed to fetch category");
    }
  }

  /**
   * Create a new global activity category
   */
  static async createCategory(
    categoryData: Omit<GlobalActivityCategory, "id" | "createdAt" | "updatedAt">
  ): Promise<string> {
    // Validation
    if (!categoryData.name?.trim()) {
      throw new Error("Category name is required");
    }

    try {
      // Get the current max order to set new category at the end
      const categories = await this.fetchCategories();
      const maxOrder = categories.reduce(
        (max, cat) => Math.max(max, cat.order || 0),
        0
      );

      const dataToSave = {
        name: categoryData.name.trim(),
        description: categoryData.description?.trim() || "",
        order: categoryData.order ?? maxOrder + 1,
        deleted: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, COLLECTION_NAME), dataToSave);
      return docRef.id;
    } catch (error) {
      console.error("Error creating category:", error);
      throw new Error("Failed to create category");
    }
  }

  /**
   * Update an existing category
   */
  static async updateCategory(
    categoryId: string,
    categoryData: Partial<GlobalActivityCategory>
  ): Promise<void> {
    if (!categoryId) {
      throw new Error("Category ID is required");
    }

    try {
      const dataToUpdate: {
        updatedAt: Timestamp;
        name?: string;
        description?: string;
        order?: number;
      } = {
        updatedAt: Timestamp.now(),
      };

      if (categoryData.name !== undefined) {
        dataToUpdate.name = categoryData.name.trim();
      }
      if (categoryData.description !== undefined) {
        dataToUpdate.description = categoryData.description.trim();
      }
      if (categoryData.order !== undefined) {
        dataToUpdate.order = categoryData.order;
      }

      await updateDoc(doc(db, COLLECTION_NAME, categoryId), dataToUpdate);
    } catch (error) {
      console.error("Error updating category:", error);
      throw new Error("Failed to update category");
    }
  }

  /**
   * Soft delete a category (sets deleted flag to true)
   */
  static async deleteCategory(categoryId: string): Promise<void> {
    if (!categoryId) {
      throw new Error("Category ID is required");
    }

    try {
      await updateDoc(doc(db, COLLECTION_NAME, categoryId), {
        deleted: true,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error("Error soft deleting category:", error);
      throw new Error("Failed to delete category");
    }
  }

  // ==================== ACTIVITY TYPE OPERATIONS ====================

  /**
   * Fetch all activity types within a category
   */
  static async fetchActivityTypes(
    categoryId: string
  ): Promise<GlobalActivityType[]> {
    if (!categoryId) {
      throw new Error("Category ID is required");
    }

    try {
      const typesRef = collection(db, COLLECTION_NAME, categoryId, "types");
      const q = query(typesRef, orderBy("name"));
      const snapshot = await getDocs(q);

      const activityTypes = snapshot.docs.map((doc) => ({
        id: doc.id,
        categoryId,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as GlobalActivityType[];

      // Filter out soft-deleted activity types
      return activityTypes.filter((activityType) => !activityType.deleted);
    } catch (error) {
      console.error("Error fetching activity types:", error);
      throw new Error("Failed to fetch activity types");
    }
  }

  /**
   * Fetch all activity types across all categories
   */
  static async fetchAllActivityTypes(): Promise<GlobalActivityType[]> {
    try {
      const categories = await this.fetchCategories();
      const allActivityTypes: GlobalActivityType[] = [];

      for (const category of categories) {
        if (category.id) {
          const types = await this.fetchActivityTypes(category.id);
          allActivityTypes.push(...types);
        }
      }

      return allActivityTypes;
    } catch (error) {
      console.error("Error fetching all activity types:", error);
      throw new Error("Failed to fetch all activity types");
    }
  }

  /**
   * Fetch activity types for multiple categories
   * Used for fetching activities from user's selected packs
   */
  static async fetchActivityTypesForCategories(
    categoryIds: string[]
  ): Promise<GlobalActivityType[]> {
    console.log("[GlobalActivityService] fetchActivityTypesForCategories called with:", categoryIds);
    
    if (!categoryIds || categoryIds.length === 0) {
      console.log("[GlobalActivityService] No category IDs provided, returning empty array");
      return [];
    }

    try {
      const allActivityTypes: GlobalActivityType[] = [];

      // Fetch activity types for each category in parallel
      console.log("[GlobalActivityService] Fetching activity types for", categoryIds.length, "categories");
      const results = await Promise.all(
        categoryIds.map(async (categoryId) => {
          console.log("[GlobalActivityService] Fetching for category:", categoryId);
          const types = await this.fetchActivityTypes(categoryId);
          console.log("[GlobalActivityService] Got", types.length, "activities for category:", categoryId);
          return types;
        })
      );

      // Flatten results
      results.forEach((types) => {
        allActivityTypes.push(...types);
      });

      console.log("[GlobalActivityService] Total activities fetched:", allActivityTypes.length);
      return allActivityTypes;
    } catch (error) {
      console.error("[GlobalActivityService] Error fetching activity types for categories:", error);
      throw new Error("Failed to fetch activity types for selected categories");
    }
  }

  /**
   * Get a single activity type by ID
   */
  static async getActivityTypeById(
    categoryId: string,
    typeId: string
  ): Promise<GlobalActivityType | null> {
    try {
      const docRef = doc(db, COLLECTION_NAME, categoryId, "types", typeId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        categoryId,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as GlobalActivityType;
    } catch (error) {
      console.error("Error fetching activity type by ID:", error);
      throw new Error("Failed to fetch activity type");
    }
  }

  /**
   * Create a new activity type within a category
   */
  static async createActivityType(
    categoryId: string,
    activityTypeData: {
      name: string;
      description: string;
      variables: ActivityVariableDefinition[];
      image?: string;
      videoUrl?: string;
      subCategories?: string[];
    }
  ): Promise<string> {
    if (!categoryId) {
      throw new Error("Category ID is required");
    }

    // Validation
    if (!activityTypeData.name?.trim()) {
      throw new Error("Activity type name is required");
    }

    if (
      !activityTypeData.variables ||
      activityTypeData.variables.length === 0
    ) {
      throw new Error("At least one variable is required");
    }

    try {
      // Clean variables to remove undefined values (Firestore doesn't accept undefined)
      const cleanedVariables = activityTypeData.variables.map((v) => {
        const cleaned: { name: string; defaultUnit: string; unitsList?: string[] } = {
          name: v.name,
          defaultUnit: v.defaultUnit,
        };
        // Only include unitsList if it has values
        if (v.unitsList && v.unitsList.length > 0) {
          cleaned.unitsList = v.unitsList;
        }
        return cleaned;
      });

      const dataToSave: Record<string, unknown> = {
        name: activityTypeData.name.trim(),
        description: activityTypeData.description?.trim() || "",
        variables: cleanedVariables,
        image: activityTypeData.image?.trim() || "",
        videoUrl: activityTypeData.videoUrl?.trim() || "",
        categoryId,
        deleted: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      if (activityTypeData.subCategories && activityTypeData.subCategories.length > 0) {
        dataToSave.subCategories = activityTypeData.subCategories;
      }

      const docRef = await addDoc(
        collection(db, COLLECTION_NAME, categoryId, "types"),
        dataToSave
      );

      return docRef.id;
    } catch (error) {
      console.error("Error creating activity type:", error);
      throw new Error("Failed to create activity type");
    }
  }

  /**
   * Update an existing activity type
   */
  static async updateActivityType(
    categoryId: string,
    typeId: string,
    activityTypeData: {
      name?: string;
      description?: string;
      variables?: ActivityVariableDefinition[];
      image?: string;
      videoUrl?: string;
      subCategories?: string[];
    }
  ): Promise<void> {
    if (!categoryId) {
      throw new Error("Category ID is required");
    }

    if (!typeId) {
      throw new Error("Activity type ID is required");
    }

    try {
      const dataToUpdate: {
        updatedAt: Timestamp;
        name?: string;
        description?: string;
        variables?: ActivityVariableDefinition[];
        image?: string;
        videoUrl?: string;
        subCategories?: string[];
      } = {
        updatedAt: Timestamp.now(),
      };

      if (activityTypeData.name !== undefined) {
        if (!activityTypeData.name.trim()) {
          throw new Error("Activity type name cannot be empty");
        }
        dataToUpdate.name = activityTypeData.name.trim();
      }

      if (activityTypeData.description !== undefined) {
        dataToUpdate.description = activityTypeData.description.trim();
      }

      if (activityTypeData.variables !== undefined) {
        if (activityTypeData.variables.length === 0) {
          throw new Error("At least one variable is required");
        }
        // Clean variables to remove undefined values (Firestore doesn't accept undefined)
        dataToUpdate.variables = activityTypeData.variables.map((v) => {
          const cleaned: { name: string; defaultUnit: string; unitsList?: string[] } = {
            name: v.name,
            defaultUnit: v.defaultUnit,
          };
          if (v.unitsList && v.unitsList.length > 0) {
            cleaned.unitsList = v.unitsList;
          }
          return cleaned;
        });
      }

      if (activityTypeData.image !== undefined) {
        dataToUpdate.image = activityTypeData.image.trim();
      }

      if (activityTypeData.videoUrl !== undefined) {
        dataToUpdate.videoUrl = activityTypeData.videoUrl.trim();
      }

      if (activityTypeData.subCategories !== undefined) {
        dataToUpdate.subCategories = activityTypeData.subCategories;
      }

      await updateDoc(
        doc(db, COLLECTION_NAME, categoryId, "types", typeId),
        dataToUpdate
      );
    } catch (error) {
      console.error("Error updating activity type:", error);
      throw new Error("Failed to update activity type");
    }
  }

  /**
   * Soft delete an activity type (sets deleted flag to true)
   */
  static async deleteActivityType(
    categoryId: string,
    typeId: string
  ): Promise<void> {
    if (!categoryId) {
      throw new Error("Category ID is required");
    }

    if (!typeId) {
      throw new Error("Activity type ID is required");
    }

    try {
      await updateDoc(doc(db, COLLECTION_NAME, categoryId, "types", typeId), {
        deleted: true,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error("Error soft deleting activity type:", error);
      throw new Error("Failed to delete activity type");
    }
  }
}

// Export singleton instance for convenience
export const globalActivityService = GlobalActivityService;
